'use client';

import { Button, Text, Stack, Alert, Loader, Paper, Group } from '@mantine/core';
import { IconBrandLinkedin, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { useState, useEffect } from 'react';

interface LinkedInConnectProps {
  mentorId?: string;
  mentorHandle?: string;
  onLinkedInConnected?: (linkedinUrl: string) => void;
  linkedinUrl?: string;
  context?: 'mentorship' | 'mentor' | 'profile';
  required?: boolean;
  onBeforeRedirect?: () => Promise<void>;
}

export default function LinkedInConnect({ 
  mentorId,
  mentorHandle,
  onLinkedInConnected, 
  linkedinUrl: initialLinkedinUrl,
  context = 'mentorship',
  required = false,
  onBeforeRedirect
}: LinkedInConnectProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [linkedinEmail, setLinkedinEmail] = useState('');
  const [linkedinEmailVerified, setLinkedinEmailVerified] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState('');

  // Check LinkedIn connection status on mount
  useEffect(() => {
    const checkConnectionStatus = async () => {
      try {
        const response = await fetch('/api/user/linkedin');
        if (response.ok) {
          const data = await response.json();
          if (data.isConnected) {
            setIsConnected(true);
            setLinkedinEmail(data.linkedinEmail);
            setLinkedinEmailVerified(data.linkedinEmailVerified);
          }
        }
      } catch (error) {
        console.error('Error checking LinkedIn connection status:', error);
      }
    };

    checkConnectionStatus();
  }, []);

  // Check for LinkedIn callback parameters on component mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const linkedinSuccess = urlParams.get('linkedin_success');
      const linkedinError = urlParams.get('linkedin_error');

      if (linkedinSuccess === 'true') {
        // LinkedIn email is now saved server-side during OAuth callback
        // Fetch the updated LinkedIn data from the database
        const fetchLinkedInData = async () => {
          try {
            const response = await fetch('/api/user/linkedin');
            if (response.ok) {
              const data = await response.json();
              if (data.isConnected) {
                setIsConnected(true);
                setLinkedinEmail(data.linkedinEmail);
                setLinkedinEmailVerified(data.linkedinEmailVerified);
                // Call callback with the actual LinkedIn URL
                onLinkedInConnected?.(data.linkedinUrl || '');
              }
            }
            setError('');
          } catch (error) {
            console.error('Error fetching LinkedIn URL:', error);
            setError('LinkedIn connected but failed to load. Please refresh the page.');
          }
        };

        fetchLinkedInData();
        
        // Clean up URL parameters
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('linkedin_success');
        newUrl.searchParams.delete('linkedin_error');
        window.history.replaceState({}, '', newUrl.toString());
      } else if (linkedinError) {
        let errorMessage = 'Failed to connect LinkedIn account';
        switch (linkedinError) {
          case 'access_denied':
            errorMessage = 'LinkedIn access was denied. Please try again.';
            break;
          case 'invalid_request':
            errorMessage = 'Invalid LinkedIn request. Please try again.';
            break;
          case 'server_error':
            errorMessage = 'Server error occurred. Please try again later.';
            break;
        }
        setError(errorMessage);
        
        // Clean up URL parameters
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('linkedin_error');
        window.history.replaceState({}, '', newUrl.toString());
      }
    }
  }, [onLinkedInConnected]);

  const handleLinkedInConnect = async () => {
    setIsConnecting(true);
    setError('');

    try {
      // Save application progress before redirecting
      if (onBeforeRedirect) {
        await onBeforeRedirect();
      }

      const response = await fetch('/api/auth/linkedin/initiate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mentorId, mentorHandle, context }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to initiate LinkedIn connection');
      }

      // Redirect to LinkedIn OAuth
      window.location.href = data.authUrl;
    } catch (error) {
      console.error('LinkedIn connection error:', error);
      setError(error instanceof Error ? error.message : 'Failed to connect LinkedIn account');
      setIsConnecting(false);
    }
  };

  // Show connected state with reconnect option
  if (isConnected) {
    return (
      <Paper p="md" radius="md" withBorder style={{ backgroundColor: '#f8fffe', boxShadow: 'none' }}>
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start">
            <div style={{ flex: 1 }}>
              <Group gap="xs" mb="xs">
                <IconCheck size={16} color="#10b981" />
                <Text size="sm" fw={500} c="green.7">
                  LinkedIn Verified
                </Text>
              </Group>
              <Text size="xs" c="dimmed" mb="xs">
                Your LinkedIn account has been verified for identity confirmation.
              </Text>
              {linkedinEmail && (
                <Text size="xs" c="dimmed">
                  Email: {linkedinEmail} {linkedinEmailVerified && '✓'}
                </Text>
              )}
            </div>
            <Button
              variant="subtle"
              size="xs"
              color="blue"
              onClick={handleLinkedInConnect}
              disabled={isConnecting}
              leftSection={isConnecting ? <Loader size={12} /> : <IconBrandLinkedin size={12} />}
            >
              {isConnecting ? 'Reconnecting...' : 'Reconnect'}
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  }

  let text = "";
  if (context === 'mentor') {
    if (required) {
      text = "LinkedIn connection is required for all mentors. This helps mentees learn about your professional background and builds trust in the mentorship relationship.";
    } else {
      text = "We'll automatically add your LinkedIn profile to your mentor profile. This helps mentees learn more about your professional background.";
    }
  } else if (context === 'mentorship') {
    if (required) {
      text = "LinkedIn connection is recommended. This helps mentors learn about your professional background and builds trust in the mentorship relationship.";
    } else {
      text = "This helps mentors learn more about your professional background.";
    }
  } else {
    if (required) {
      text = "LinkedIn connection is recommended. This helps your mentor/mentees learn about your professional background and builds trust in the mentorship relationship.";
    } else {
      text = "This helps your mentor/mentees learn more about your professional background.";
    }
  }

  return (
    <Paper p="md" radius="md" withBorder style={{ backgroundColor: '#fafafa', boxShadow: 'none' }}>
      <Stack gap="sm">
        <div>
          <Text size="sm" fw={500} mb={6}>
            Connect Your LinkedIn Profile{required && <Text component="span" c="red"> *</Text>}
          </Text>
          <Text size="xs" c="dimmed" mb="sm">
            {text}
          </Text>
        </div>

        {error && (
          <Alert 
            icon={<IconAlertCircle size={16} />} 
            title="Connection Failed" 
            color="red"
            variant="light"
          >
            <Text size="sm">{error}</Text>
          </Alert>
        )}

        <Button
          leftSection={isConnecting ? <Loader size={16} /> : <IconBrandLinkedin size={16} />}
          onClick={handleLinkedInConnect}
          disabled={isConnecting}
          variant="outline"
          color="blue"
          size="md"
          fullWidth
          style={{
            borderColor: '#0077B5',
            color: '#0077B5',
            marginTop: '0.5rem',
          }}
        >
          {isConnecting ? 'Connecting...' : 'Connect LinkedIn Account'}
        </Button>
      </Stack>
    </Paper>
  );
}
